import { styled, alpha } from '@mui/material/styles';
import { AppBar, AppBarProps, Box, Avatar } from '@mui/material';
import _ from 'lodash';

interface AppBarProp extends AppBarProps {
	open: boolean;
	mobile?: boolean;
	nosidebar?: boolean | undefined | null;
	dir?: string;
}

export const CustomAppBar = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })<AppBarProp>(
	({ theme, open, mobile, nosidebar, dir }) => ({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		lineHeight: 0,
		right: dir === 'rtl' ? 0 : 'auto',
		[theme.breakpoints.down('sm')]: {
			marginLeft: 'auto',
			width: '100%',
			borderBottom: '1px solid rgb(0 71 81)',
			'& .desktop-item': {
				display: 'none',
			},
			padding: '0 16px',
		},
		marginLeft: mobile || nosidebar ? 'auto' : `${theme.sizes.DRAWER_WIDTH_CLOSED + 3}px`,
		padding: '0 24px',
		boxShadow: `inset 0 -1px 0 0 rgba(47, 229, 207, 0.05)`,
		backgroundColor: theme.palette.nav.primary,
		width: mobile || nosidebar ? '100%' : `calc(100% - ${theme.sizes.DRAWER_WIDTH_CLOSED}px)`,
		height: `${theme.sizes.APPBAR_MOBILE}px`,
		transition: theme.transitions.create(['margin', 'width'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		...(open && {
			right: dir === 'rtl' ? 0 : 'auto',
			width: nosidebar ? '100%' : `calc(100% - ${theme.sizes.DRAWER_WIDTH}px)`,
			marginLeft: nosidebar ? 'auto' : `${theme.sizes.DRAWER_WIDTH}px`,
			transition: theme.transitions.create(['margin', 'width'], {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.enteringScreen,
			}),
		}),
	})
);

export const ShadowBox = styled('div')<AppBarProp>((props) => ({
	'& .MuiBadge-root': {
		padding: '8px',
		borderRadius: '8px',
		//background: "aliceblue",
		position: 'relative',
		backgroundColor: props.open ? props.theme.palette.root.brandPrimary_008 : 'transparent',
		'&:hover': {
			backgroundColor: props.theme.palette.root.brandPrimary_008,
		},
	},
}));

export const CustomBadge = styled('div')((props) => ({
	'& .MuiBadge-badge': {
		width: '9px',
		position: 'absolute',
		top: '19%',
		right: '20%',
		color: props.theme.palette.root.basicWhite,
		backgroundColor: props.theme.palette.root.orange,
	},
}));

export const CustomBadgeDot = styled('div')((props) => ({
	'& .MuiBadge-badge': {
		fontSize: 0,
		transform: 'scale(0.5)',
		border: `3px solid ${props.theme.palette.root.basicWhite}`,
	},
}));

export const CustomBadgeSum = styled('div')((props) => ({
	'& .MuiBadge-root': {
		transform: 'scale(1.2)',
		margin: '0 15px',
	},
	'& .MuiBadge-badge': {
		fontSize: '10px',
	},
}));

export const Search = styled('div')((props) => ({
	position: 'relative',
	borderRadius: props.theme.shape.borderRadius,
	backgroundColor: alpha(props.theme.palette.grey[500], 0.65),
	'&:hover': {
		backgroundColor: alpha(props.theme.palette.grey[500], 0.75),
	},
	marginLeft: 0,
	textAlign: 'left',
	width: '100%',
	[props.theme.breakpoints.up('sm')]: {
		margin: props.theme.spacing(1),
		width: 'auto',
	},
}));

export const SearchIconWrapper = styled('div')((props) => ({
	padding: props.theme.spacing(0, 2),
	height: '100%',
	position: 'absolute',
	pointerEvents: 'none',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	left: _.isEqual(props.dir, 'rtl') ? 0 : 'auto',
}));

export const ListWrapper = styled('div')((props) => ({
	position: 'absolute',
	width: '30%',
	height: '300px',
	overflowY: 'scroll',
	overflowX: 'hidden',
	top: '55px',
	background: '#FFFFFF',
	border: ' 1px solid #CBDCDE',
	boxShadow: '0px 4px 12px rgba(44, 62, 80, 0.16)',
	borderRadius: '8px',
	color: '#2C3E50',
}));

export const ProfileAvatar = styled(Avatar)(({ theme }) => ({
	background: theme.palette.root.basicDeeoGreen,
}));

export const MenuAvatar = styled(Box)(({ theme }) => ({
	background: theme.palette.root.darkGrayed_028,
	borderRadius: '100%',
	width: '32px',
	height: '32px',
	marginRight: '8px',
	'&.selected': {
		background: theme.palette.root.basicWhite,
		textAlign: 'center',
		'& svg': {
			height: '100%',
			width: '17px',
			color: theme.palette.root.brandSecondary,
		},
	},
}));

export const NavIcon = styled(Box)(({ theme }) => ({
	cursor: 'pointer',
	marginLeft: '8px',
	width: '40px',
	height: '40px',
	borderRadius: '8px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	'&:hover': {
		backgroundColor: theme.palette.root.brandPrimary_008,
	},
}));

export const ProfileName = styled(Box)(({ theme }) => ({
	lineHeight: 1.5,
	color: theme.palette.root.basicDeeoGreen,
	marginLeft: '24px',
	textAlign: 'right',
}));

export const AutocompleteStyle = {
	'& .MuiAutocomplete-inputRoot': {
		px: 1,
		py: 0,
	},
};

export const AvatarMenu = styled(Box)(({ theme }) => ({
	'& li': {
		width: '224px',
		[theme.breakpoints.down('sm')]: { width: 'auto' },
		margin: '0 8px',
		padding: '8px',
		borderRadius: '8px',
		'&.selected': {
			background: theme.palette.root.brandSecondary_012,
		},
	},
}));

export const MenuRole = styled(Box)(({ theme }) => ({
	height: '32px',
	'& .MuiTypography-body2': {
		lineHeight: 1,
		fontFamily: 'RubikRegular',
	},
	'& .MuiTypography-caption': {
		color: '#5d777b',
	},
}));

export const ResultsLength = styled(Box)(({ theme }) => ({
	'& .MuiBox-root': {
		fontSize: '12px',
		color: '#5D777B',
		borderBottom: '2px solid #F3F7F7',
		padding: '15px',
	},
}));

export const MobileSearchWrapper = styled('div')((props) => ({
	position: 'fixed',
	width: '100%',
	left: '0px',
	top: '0px',
	height: '100%',
	background: '#FFFFFF',
	color: '#004751',
	zIndex: 1,
}));

export const MobileBottomNavbar = styled('div')((props) => ({
	position: 'fixed',
	width: '100%',
	left: '0px',
	bottom: '0px',
	height: '96px',
	background: '#FFFFFF',
	color: '#008A69',
	zIndex: 2,
	boxShadow: 'inset 0px 1px 0px #E7EDEE',
}));

export const IconBox = styled('div')((props) => ({
	flex: '1',
	textAlign: 'center',
	paddingTop: '15px',
	'& img': {
		marginBottom: '7px',
	},
}));
